import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Browser Web Speech API wrappers.
 *
 * useSpeechRecognition — mic input (speech-to-text). The Recognition object
 * is initialized once and kept stable; the onResult callback is stored in a
 * ref so re-renders don't recreate the object.
 *
 * useSpeechSynthesis — kept for backwards compatibility but no longer used
 * by the chat (Onyx TTS replaces it).
 */

function getRecognition() {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function useSpeechRecognition({ onResult, lang = "en-US", continuous = false } = {}) {
    const [supported, setSupported] = useState(false);
    const [listening, setListening] = useState(false);
    const recRef = useRef(null);
    const onResultRef = useRef(onResult);
    onResultRef.current = onResult;
    const wantListeningRef = useRef(false);
    const continuousRef = useRef(continuous);
    continuousRef.current = continuous;
    // Persistent finals across auto-restarts inside one listening session.
    // Each time the browser auto-ends a non-continuous recognition (because
    // the speaker paused), we keep the finalised text here so the next
    // restarted session is concatenated onto it. Reset on start()/abort().
    const sessionFinalRef = useRef("");
    // Hard watchdog: if the mic stays open for this many ms without any
    // final transcript landing, we auto-stop so the UI never gets stuck
    // in listening mode after the user walks away or the recognizer
    // silently fails to fire any event.
    const watchdogRef = useRef(null);
    const MAX_LISTEN_MS = 25000;
    const armWatchdog = () => {
        if (watchdogRef.current) clearTimeout(watchdogRef.current);
        watchdogRef.current = setTimeout(() => {
            wantListeningRef.current = false;
            const rec = recRef.current;
            if (rec) {
                try { rec.abort(); } catch (_e) { /* ignore */ }
            }
            setListening(false);
        }, MAX_LISTEN_MS);
    };
    const clearWatchdog = () => {
        if (watchdogRef.current) {
            clearTimeout(watchdogRef.current);
            watchdogRef.current = null;
        }
    };

    useEffect(() => {
        const Ctor = getRecognition();
        if (!Ctor) {
            setSupported(false);
            return;
        }
        setSupported(true);
        const rec = new Ctor();
        rec.continuous = !!continuousRef.current;
        rec.interimResults = true;
        rec.lang = lang;
        rec.onstart = () => setListening(true);
        rec.onend = () => {
            // Only flip the UI to "not listening" when the caller actually
            // wants us to stop. Otherwise we're about to auto-restart and the
            // icon would flicker on every speech pause — which is exactly the
            // "on and off constantly" behaviour the mic was showing.
            if (!wantListeningRef.current || continuousRef.current) {
                setListening(false);
            }
            // Re-arm only if caller still wants listening AND we're NOT in
            // native continuous mode.
            if (wantListeningRef.current && !continuousRef.current) {
                setTimeout(() => {
                    try {
                        rec.start();
                    } catch (_e) {
                        // already starting
                    }
                }, 200);
            } else {
                clearWatchdog();
            }
        };
        rec.onerror = (event) => {
            setListening(false);
            const err = event?.error;
            // Hard errors: permission denied, audio capture failure, the
            // service refusing to start. Don't try to recover — clear the
            // intent so onend's auto-restart doesn't loop the mic forever.
            if (
                err === "not-allowed" ||
                err === "service-not-allowed" ||
                err === "audio-capture" ||
                err === "network" ||
                err === "aborted" ||
                err === "no-speech"
            ) {
                wantListeningRef.current = false;
                clearWatchdog();
                if (err === "not-allowed" || err === "service-not-allowed") {
                    console.warn("Mic permission denied:", err);
                }
            }
            // Other errors (bad-grammar, language-not-supported) — let onend
            // decide whether to retry.
        };
        rec.onresult = (event) => {
            // Read FULL current state from the recognizer; never concatenate
            // event-by-event. The browser maintains a single growing results
            // array per recognition session, so reading all of it here gives
            // the true cumulative transcript for THIS session.
            //
            // We collect finals into an array first, then dedupe consecutive
            // duplicates. Some browsers (notably Chrome on mobile, in
            // continuous mode) emit the same finalised phrase twice or
            // three times in event.results — which used to show up in the
            // input field as "hello hello hello".
            const finals = [];
            let interim = "";
            for (let i = 0; i < event.results.length; i++) {
                const r = event.results[i];
                const t = (r[0]?.transcript || "").trim();
                if (!t) continue;
                if (r.isFinal) {
                    if (finals.length === 0 || finals[finals.length - 1] !== t) {
                        finals.push(t);
                    }
                } else {
                    interim += t + " ";
                }
            }
            let sessionFinal = finals.join(" ").replace(/\s+/g, " ").trim();
            interim = interim.replace(/\s+/g, " ").trim();
            // In CONTINUOUS mode the browser keeps the same session open and
            // event.results already contains EVERY final this session has
            // produced — so concatenating with sessionFinalRef would cause
            // each newly-finalised phrase to appear twice (then three times,
            // then four…). Only fold in the prior buffer when we're in the
            // non-continuous auto-restart path, where each restart starts a
            // brand-new event.results array.
            const isContinuous = continuousRef.current;
            const prior = isContinuous ? "" : sessionFinalRef.current;
            const finalAll = [prior, sessionFinal].filter(Boolean).join(" ").trim();
            const combined = [finalAll, interim].filter(Boolean).join(" ").trim();
            if (onResultRef.current) {
                onResultRef.current(combined, finalAll.length > 0);
            }
            if (sessionFinal && !isContinuous) {
                sessionFinalRef.current = finalAll;
            }
            if (sessionFinal) {
                clearWatchdog();
            }
        };
        recRef.current = rec;
        return () => {
            wantListeningRef.current = false;
            clearWatchdog();
            try {
                rec.abort();
            } catch (_e) {
                // ignore
            }
        };
    }, [lang]);

    const start = useCallback(() => {
        const rec = recRef.current;
        if (!rec) return false;
        wantListeningRef.current = true;
        // Clear the cross-session final buffer — a fresh user turn begins now.
        sessionFinalRef.current = "";
        armWatchdog();
        try {
            rec.start();
            return true;
        } catch (_e) {
            return true;
        }
    }, []);

    const stop = useCallback(() => {
        const rec = recRef.current;
        wantListeningRef.current = false;
        clearWatchdog();
        if (!rec) return;
        try {
            rec.stop();
        } catch (_e) {
            // ignore
        }
    }, []);

    const abort = useCallback(() => {
        const rec = recRef.current;
        wantListeningRef.current = false;
        sessionFinalRef.current = "";
        clearWatchdog();
        if (!rec) return;
        try {
            rec.abort();
        } catch (_e) {
            // ignore
        }
    }, []);

    return { supported, listening, start, stop, abort };
}

export function useSpeechSynthesis() {
    const [supported] = useState(
        typeof window !== "undefined" &&
            typeof window.speechSynthesis !== "undefined"
    );
    return { supported, speaking: false, speak: () => {}, stop: () => {} };
}


/**
 * Silent voice input — MediaRecorder + RMS silence detection + Whisper.
 * Replaces the browser's SpeechRecognition for the inline mic so the user
 * gets ZERO chime / beep sounds. Visual glow alone signals "listening".
 *
 * Flow: start() → request mic → MediaRecorder records WebM/Opus → an
 * AudioContext analyser watches RMS and detects ~`silenceMs` of quiet → we
 * stop the recorder → ship the blob to /api/transcribe → call onResult with
 * the transcript. The user can also tap stop() to finalize immediately.
 */
export function useSilentMic({ onResult, silenceMs = 2200, maxMs = 30000, transcribe } = {}) {
    const [supported, setSupported] = useState(true);
    const [listening, setListening] = useState(false);
    const [busy, setBusy] = useState(false);

    const streamRef = useRef(null);
    const recRef = useRef(null);
    const ctxRef = useRef(null);
    const rafRef = useRef(null);
    const silenceStartRef = useRef(null);
    const heardSpeechRef = useRef(false);
    const watchdogRef = useRef(null);
    const onResultRef = useRef(onResult);
    onResultRef.current = onResult;

    useEffect(() => {
        const ok =
            typeof navigator !== "undefined" &&
            !!navigator.mediaDevices &&
            typeof MediaRecorder !== "undefined";
        setSupported(ok);
    }, []);

    const cleanup = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (watchdogRef.current) {
            clearTimeout(watchdogRef.current);
            watchdogRef.current = null;
        }
        try {
            ctxRef.current?.close();
        } catch (_e) {
            /* ignore */
        }
        ctxRef.current = null;
        try {
            streamRef.current?.getTracks().forEach((t) => t.stop());
        } catch (_e) {
            /* ignore */
        }
        streamRef.current = null;
        recRef.current = null;
        silenceStartRef.current = null;
        heardSpeechRef.current = false;
    }, []);

    const start = useCallback(async () => {
        if (listening || busy) return false;
        if (!navigator?.mediaDevices?.getUserMedia) return false;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const Ctor =
                typeof MediaRecorder !== "undefined" &&
                ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(
                    (m) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)
                );
            const rec = Ctor
                ? new MediaRecorder(stream, { mimeType: Ctor })
                : new MediaRecorder(stream);
            const chunks = [];
            rec.ondataavailable = (ev) => {
                if (ev.data && ev.data.size > 0) chunks.push(ev.data);
            };
            rec.onstop = async () => {
                const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
                cleanup();
                setListening(false);
                if (!heardSpeechRef.current || blob.size < 1500 || !transcribe) {
                    setBusy(false);
                    return;
                }
                setBusy(true);
                try {
                    const ext = (rec.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
                    const data = await transcribe(blob, `voice.${ext}`);
                    const text = (data?.text || "").trim();
                    if (text && onResultRef.current) {
                        onResultRef.current(text, true);
                    }
                } catch (_e) {
                    /* transcription failed — silently ignore so the user can retry */
                } finally {
                    setBusy(false);
                }
            };
            recRef.current = rec;
            rec.start(250);
            setListening(true);

            // RMS analyser
            const Cx = window.AudioContext || window.webkitAudioContext;
            const ctx = new Cx();
            ctxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);
            const buf = new Uint8Array(analyser.fftSize);

            const SPEECH_RMS = 0.06;
            const tick = () => {
                if (!recRef.current || recRef.current.state !== "recording") {
                    return;
                }
                analyser.getByteTimeDomainData(buf);
                let sum = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = (buf[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / buf.length);
                const now = Date.now();
                if (rms > SPEECH_RMS) {
                    heardSpeechRef.current = true;
                    silenceStartRef.current = null;
                } else if (heardSpeechRef.current) {
                    if (!silenceStartRef.current) silenceStartRef.current = now;
                    if (now - silenceStartRef.current >= silenceMs) {
                        try {
                            recRef.current.stop();
                        } catch (_e) {
                            /* ignore */
                        }
                        return;
                    }
                }
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);

            watchdogRef.current = setTimeout(() => {
                try {
                    recRef.current?.stop();
                } catch (_e) {
                    /* ignore */
                }
            }, maxMs);
            return true;
        } catch (_e) {
            cleanup();
            setListening(false);
            setBusy(false);
            return false;
        }
    }, [listening, busy, cleanup, silenceMs, maxMs, transcribe]);

    const stop = useCallback(() => {
        const rec = recRef.current;
        if (!rec) {
            cleanup();
            setListening(false);
            return;
        }
        if (rec.state === "recording") {
            // Force-mark as having heard speech so the trailing handler
            // actually sends to Whisper rather than silently discarding it.
            heardSpeechRef.current = heardSpeechRef.current || true;
            try {
                rec.stop();
            } catch (_e) {
                /* ignore */
            }
        }
    }, [cleanup]);

    const abort = useCallback(() => {
        const rec = recRef.current;
        if (rec && rec.state === "recording") {
            heardSpeechRef.current = false; // drop transcript silently
            try {
                rec.stop();
            } catch (_e) {
                /* ignore */
            }
        } else {
            cleanup();
            setListening(false);
        }
    }, [cleanup]);

    useEffect(() => () => cleanup(), [cleanup]);

    return { supported, listening, busy, start, stop, abort };
}
